/// <reference types="vite/client" />
/**
 * Admin environment registry — lets the dashboard point its Supabase data source
 * at either PRODUCTION (the default, from VITE_SUPABASE_*) or a DEMO backend
 * (from the optional VITE_DEMO_SUPABASE_*), for live demos that must not touch
 * prod. The choice is persisted in localStorage and applied with a full reload:
 * supabaseClient.ts reads the active env at import time, and a hard reload
 * rebuilds the Refine data/auth/live providers against the new backend.
 *
 * `resolveAdminEnvironments` + `pickActiveEnv` are pure so they can be unit-tested
 * without juggling `import.meta.env` (inlined at build time). Sessions isolate
 * automatically: supabase-js derives its auth storageKey from the URL, so prod
 * and demo never share a session.
 */

export type AdminEnvId = 'prod' | 'demo'

export interface AdminEnvironment {
  id: AdminEnvId
  label: string
  url: string
  anonKey: string
  isProd: boolean
}

export interface AdminEnvInputs {
  url: string
  anonKey: string
  demoUrl: string
  demoAnonKey: string
}

export interface ResolvedAdminEnvironments {
  /** Always `[prod]`, or `[prod, demo]` when the demo vars are present. */
  environments: AdminEnvironment[]
  demoAvailable: boolean
}

const present = (v: string | undefined): boolean => !!v && v.trim().length > 0

/** Pure registry builder — no `import.meta.env` / localStorage reads. */
export function resolveAdminEnvironments(inputs: AdminEnvInputs): ResolvedAdminEnvironments {
  const prod: AdminEnvironment = {
    id: 'prod',
    label: 'Production',
    url: inputs.url,
    anonKey: inputs.anonKey,
    isProd: true,
  }
  const demoAvailable = present(inputs.demoUrl) && present(inputs.demoAnonKey)
  if (!demoAvailable) return { environments: [prod], demoAvailable: false }
  const demo: AdminEnvironment = {
    id: 'demo',
    label: 'Demo',
    url: inputs.demoUrl,
    anonKey: inputs.demoAnonKey,
    isProd: false,
  }
  return { environments: [prod, demo], demoAvailable: true }
}

/** Which env is active given the stored value — demo only when stored AND available. */
export function pickActiveEnv(stored: string | null, demoAvailable: boolean): AdminEnvId {
  return stored === 'demo' && demoAvailable ? 'demo' : 'prod'
}

/**
 * Pure predicate: does this resolved Supabase URL point at a LOCAL backend?
 * A localhost / loopback host (localhost, 127.0.0.1, 0.0.0.0, [::1]) = LOCAL ("DEV");
 * anything else (e.g. a hosted `*.supabase.co`) = remote/"LIVE". Falls back to a
 * substring check when the URL can't be parsed so a malformed value never crashes
 * the header. Used to render the DEV/LIVE badge so the two look-alike dashboards
 * (dev :3001 on 127.0.0.1 vs prod :4173 on the hosted project) are glanceable apart.
 */
export function isLocalBackend(url: string | null | undefined): boolean {
  if (!url) return false
  const localHosts = new Set(['localhost', '127.0.0.1', '0.0.0.0', '[::1]', '::1'])
  try {
    const host = new URL(url).hostname
    return localHosts.has(host) || host.endsWith('.localhost')
  } catch {
    const lower = url.toLowerCase()
    return ['localhost', '127.0.0.1', '0.0.0.0', '::1'].some((h) => lower.includes(h))
  }
}

const resolved = resolveAdminEnvironments({
  url: (import.meta.env.VITE_SUPABASE_URL as string) ?? '',
  anonKey: (import.meta.env.VITE_SUPABASE_ANON_KEY as string) ?? '',
  demoUrl: (import.meta.env.VITE_DEMO_SUPABASE_URL as string) ?? '',
  demoAnonKey: (import.meta.env.VITE_DEMO_SUPABASE_ANON_KEY as string) ?? '',
})

export const ENVIRONMENTS = resolved.environments
export const DEMO_AVAILABLE = resolved.demoAvailable
export const ACTIVE_ENV_KEY = 'muaa.admin.env.v1'

/**
 * Credentials prefilled on the admin login while pointed at the demo backend, so
 * staff can sign straight in. Defaults to the seeded demo admin
 * (backend/supabase/seed.sql); override per build via VITE_DEMO_LOGIN_*.
 */
export const DEMO_LOGIN = {
  email: (import.meta.env.VITE_DEMO_LOGIN_EMAIL as string) || 'admin@muaa.test',
  password: (import.meta.env.VITE_DEMO_LOGIN_PASSWORD as string) || 'jaguars1917',
}

export function getActiveEnvId(): AdminEnvId {
  let stored: string | null = null
  try {
    stored = window.localStorage.getItem(ACTIVE_ENV_KEY)
  } catch {
    /* localStorage unavailable — default to prod */
  }
  return pickActiveEnv(stored, DEMO_AVAILABLE)
}

export function getActiveEnv(): AdminEnvironment {
  const id = getActiveEnvId()
  return ENVIRONMENTS.find((e) => e.id === id) ?? ENVIRONMENTS[0]
}

/** Persist the chosen backend and hard-reload so every provider rebuilds against it. */
export function switchAdminEnv(id: AdminEnvId): void {
  try {
    window.localStorage.setItem(ACTIVE_ENV_KEY, id)
  } catch {
    /* best-effort */
  }
  window.location.reload()
}
